import { TagCommanderService } from 'features/tagCommander/business/TagCommander.service';
import { useTagPlan } from 'features/tagPlan/business/useTagPlan';
import { Trans } from 'next-i18next';
import React, { ComponentPropsWithRef, FC, useMemo } from 'react';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { DownloadAppButtons } from '@components/LandingPageApp/DownloadAppButtons/DownloadAppButtons.component';
import { FAQs } from '@components/LandingPageApp/FAQs/FAQs.component';
import { LandingPageAppCard } from '@components/LandingPageApp/LandingPageAppCard.component.tsx/LandingPageAppCard.component';
import { LandingPageAppHeader } from '@components/LandingPageApp/LandingPageAppHeader/LandingPageAppHeader.component';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { ImageSrc } from '@utils/images';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';
import styles from './LandingPageAppLayout.module.scss';

type Props = ComponentPropsWithRef<'main'>;

const lists = [
  [
    {
      img: 'landing-app-functionality-meteo',
      title: 'landing-app.body.functionality-1.title',
    },
    {
      img: 'landing-app-functionality-radar',
      title: 'landing-app.body.functionality-2.title',
    },
    {
      img: 'landing-app-functionality-graphics',
      title: 'landing-app.body.functionality-3.title',
    },
  ],
  [
    {
      content: 'landing-app.body.functionality-4.desc',
      img: 'landing-app-functionality-journal',
      title: 'landing-app.body.functionality-4.title',
    },
    {
      content: 'landing-app.body.functionality-5.desc',
      img: 'landing-app-functionality-cours',
      mention: 'landing-app.body.functionality-5.mention',
      title: 'landing-app.body.functionality-5.title',
    },
    {
      content: 'landing-app.body.functionality-6.desc',
      img: 'landing-app-functionality-notifications',
      title: 'landing-app.body.functionality-6.title',
    },
  ],
];

const LandingPageAppLayout: FC<Props> = ({ className, ...rest }) => {
  const { t } = useTranslation(['landing-app']);
  const { tagClick } = useTagPlan();
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const rootClass = classnames('plc-page', styles['plc-landing-layout'], className);

  function handleTagClick(
    clickName: string,
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>
  ) {
    tagClick({ level2: ATLevel2.App, tagPageNameI18nArray: [{ code: clickName, notI18n: true }] });
    TagCommanderService.triggerEvent('click', event.currentTarget, {
      button_name: clickName,
    });
  }

  const tagProps: PlcTagProps = useMemo(
    () => ({
      level2: ATLevel2.App,
      tagPageNameI18nArray: ['landing-app.tag.title'],
    }),
    []
  );

  return (
    <PlcPageLayout className={rootClass} metaTitleI18n="landing-app.meta-title" tagProps={tagProps} {...rest}>
      <LandingPageAppHeader handleTagClick={handleTagClick} />
      <section className="flexCenter column">
        <Text
          alignment="center"
          flavour="meteo-dark-green"
          i18nKey="landing-app.sub-title"
          tag="h3"
          variant={isMobile ? 'new-h3' : 'h3'}
        />
        {lists.map((singleList) => {
          return (
            <>
              <div className="grid-m-3-s-1">
                {singleList.map((elem: { content?: string; img: string; title: string; mention?: string }) => {
                  const { img, title, content, mention } = elem;
                  const body = <>{content && <Text className="plc-pb-xs" i18nKey={content} variant="small" />}</>;
                  return (
                    <LandingPageAppCard
                      cardContent={body}
                      cardImage={img as ImageSrc}
                      cardMention={mention}
                      cardTitle={title}
                      key="landing-page-app-card"
                    />
                  );
                })}
              </div>
              <DownloadAppButtons handleTagClick={handleTagClick} />
            </>
          );
        })}
      </section>
      <FAQs />
      <section>
        <Text i18nKey="landing-app.contact-us.title" tag="h3" variant={isMobile ? 'new-h3' : 'h3'} />
        <Text i18nKey="landing-app.contact-us.desc" tag="p" variant="p" />
        <PlcAnchor linkProps={{ href: urlEnum.contactUs }}>
          <PrimaryButton
            isDarkGreen
            isSmall
            onClick={(event) => handleTagClick(t('landing-app.contact-us.button'), event)}
            textProps={{ i18nKey: 'landing-app.contact-us.button' }}
          />
        </PlcAnchor>
      </section>
      <section>
        <Text
          className="plc-pt-m"
          i18nKey="landing-app.footer.download.title"
          tag="h2"
          variant={isMobile ? 'h3' : 'h2'}
        />
        <Text i18nKey="landing-app.footer.download.desc" tag="p" variant="p" />
        <DownloadAppButtons handleTagClick={handleTagClick} />
        <Text className="plc-my-s" variant="tiny">
          <Trans i18nKey="landing-app.footer.download.mention" ns="landing-app" style={{ fontStyle: 'italic' }} />
        </Text>
      </section>
    </PlcPageLayout>
  );
};

export { LandingPageAppLayout };
